/**
 * Executive Dossier View - Main container for the full-page dossier.
 *
 * Combines the snapshot (Page 1) with drill-down sections.
 */

import { ExecutiveDetail } from './types';
import DossierSnapshot from './DossierSnapshot';
import CollapsibleSection from './CollapsibleSection';
import SourceIndex from './SourceIndex';

interface ExecutiveDossierViewProps {
  executive: ExecutiveDetail;
  onBack: () => void;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString();
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function ExecutiveDossierView({ executive, onBack }: ExecutiveDossierViewProps) {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="text-sm font-medium">Back to Leadership</span>
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Print / Export
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page 1: Snapshot */}
        <DossierSnapshot executive={executive} />

        {/* Drill-Down Sections */}
        <div className="mt-8 space-y-4">
          {/* Identity & Background */}
          <CollapsibleSection
            title="Identity & Background"
            subtitle="Education, credentials, residences"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
          >
            <div className="space-y-6">
              {/* Education */}
              {executive.education && executive.education.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Education</h4>
                  <div className="space-y-3">
                    {executive.education.map((edu, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500 mt-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{edu.institution}</p>
                          {edu.degree && (
                            <p className="text-sm text-gray-600">{edu.degree}{edu.field_of_study ? ` in ${edu.field_of_study}` : ''}</p>
                          )}
                          {edu.graduation_year && (
                            <p className="text-xs text-gray-400">Class of {edu.graduation_year}</p>
                          )}
                          {edu.honors && (
                            <p className="text-xs text-primary-600">{edu.honors}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No education data */}
              {(!executive.education || executive.education.length === 0) && (
                <p className="text-sm text-gray-500 italic">Education information not available</p>
              )}
            </div>
          </CollapsibleSection>

          {/* Career Chronology */}
          <CollapsibleSection
            title="Career Chronology"
            subtitle="Employment history with outcomes"
            defaultOpen
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
            badge={
              executive.career_history && (
                <span className="text-xs text-gray-500">{executive.career_history.length} positions</span>
              )
            }
          >
            {executive.career_history && executive.career_history.length > 0 ? (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gray-200" />

                <div className="space-y-6">
                  {executive.career_history.map((position, idx) => (
                    <div key={idx} className="relative pl-8">
                      {/* Timeline dot */}
                      <div className={`absolute left-1.5 top-1 w-3 h-3 rounded-full ${position.is_current ? 'bg-primary-600' : 'bg-gray-300'}`} />

                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">{position.title}</h4>
                            <p className="text-sm text-gray-600">{position.company_name}</p>
                          </div>
                          <span className="text-xs text-gray-500">
                            {position.start_date ? new Date(position.start_date).getFullYear() : '?'} -{' '}
                            {position.is_current ? 'Present' : position.end_date ? new Date(position.end_date).getFullYear() : '?'}
                          </span>
                        </div>
                        {position.responsibilities && (
                          <p className="mt-2 text-xs text-gray-600">
                            {position.responsibilities}
                          </p>
                        )}
                        {position.achievements && position.achievements.length > 0 && (
                          <ul className="mt-2 text-xs text-green-700 space-y-1">
                            {position.achievements.map((a, i) => (
                              <li key={i} className="flex items-start gap-1">
                                <span>+</span>
                                <span>{a}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">Career history not available</p>
            )}
          </CollapsibleSection>

          {/* Legal & Regulatory */}
          <CollapsibleSection
            title="Legal & Regulatory"
            subtitle="Litigation, enforcement, sanctions screening"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
            }
            badge={
              executive.sanctions_screen?.match_found ? (
                <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                  Alert
                </span>
              ) : executive.sanctions_screen ? (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                  Clear
                </span>
              ) : null
            }
          >
            <div className="space-y-4">
              {/* Sanctions Screening */}
              <div className="rounded-lg border border-gray-200 p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Sanctions Screening</h4>
                {executive.sanctions_screen ? (
                  <div className={`rounded-lg p-3 ${executive.sanctions_screen.match_found ? 'bg-red-50' : 'bg-green-50'}`}>
                    <div className="flex items-center gap-2">
                      {executive.sanctions_screen.match_found ? (
                        <>
                          <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm font-medium text-red-800">Match Found</span>
                        </>
                      ) : (
                        <>
                          <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm font-medium text-green-800">No Matches</span>
                        </>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      Screened: {executive.sanctions_screen.name_searched}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Last checked: {formatDate(executive.sanctions_screen.screened_at)}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border-2 border-dashed border-gray-200 p-4 text-center">
                    <p className="text-sm text-gray-500">Sanctions screening not yet completed</p>
                    <button className="mt-2 text-sm text-primary-600 hover:text-primary-500 font-medium">
                      Screen Now
                    </button>
                  </div>
                )}
              </div>

              {/* Legal Records */}
              {executive.legal_records && executive.legal_records.length > 0 ? (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Legal Records</h4>
                  <div className="space-y-3">
                    {executive.legal_records.map((record, idx) => (
                      <div key={idx} className="rounded-lg border border-gray-200 p-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
                              record.record_type === 'enforcement' ? 'bg-red-100 text-red-700' :
                              record.record_type === 'litigation' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {record.record_type}
                            </span>
                            <p className="text-sm font-medium text-gray-900 mt-1">{record.court_or_agency}</p>
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(record.filing_date)}</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">{record.description}</p>
                        <p className="text-xs text-gray-500 mt-1">Status: {record.status}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No legal records found</p>
              )}
            </div>
          </CollapsibleSection>

          {/* Financial Position */}
          <CollapsibleSection
            title="Financial Position"
            subtitle="Equity holdings, insider transactions"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          >
            <div className="space-y-4">
              {/* Equity Holdings */}
              {executive.equity_holdings && (
                <div className="rounded-lg border border-gray-200 p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Equity Holdings</h4>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(executive.equity_holdings.value)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    As of {formatDate(executive.equity_holdings.as_of_date)}
                    {executive.equity_holdings.is_estimated && ' (Estimated)'}
                  </p>
                </div>
              )}

              {/* Insider Transactions */}
              {executive.insider_transactions && executive.insider_transactions.length > 0 ? (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Insider Transactions</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead>
                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <th className="py-2">Date</th>
                          <th className="py-2">Type</th>
                          <th className="py-2 text-right">Shares</th>
                          <th className="py-2 text-right">Price</th>
                          <th className="py-2 text-right">Value</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {executive.insider_transactions.slice(0, 10).map((tx, idx) => (
                          <tr key={idx}>
                            <td className="py-2 text-gray-600">{formatDate(tx.transaction_date)}</td>
                            <td className="py-2">
                              <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${
                                tx.transaction_type.toLowerCase().includes('buy') ? 'bg-green-100 text-green-700' :
                                tx.transaction_type.toLowerCase().includes('sell') ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {tx.transaction_type}
                              </span>
                            </td>
                            <td className="py-2 text-right text-gray-900">{tx.shares.toLocaleString()}</td>
                            <td className="py-2 text-right text-gray-600">${tx.price_per_share.toFixed(2)}</td>
                            <td className="py-2 text-right font-medium text-gray-900">{formatCurrency(tx.total_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No insider transactions on record</p>
              )}
            </div>
          </CollapsibleSection>

          {/* Network & Affiliations */}
          <CollapsibleSection
            title="Network & Affiliations"
            subtitle="Board seats, associations"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
            badge={
              executive.board_memberships && (
                <span className="text-xs text-gray-500">{executive.board_memberships.length} boards</span>
              )
            }
          >
            {executive.board_memberships && executive.board_memberships.length > 0 ? (
              <div className="space-y-3">
                {executive.board_memberships.map((board, idx) => (
                  <div key={idx} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-b-0">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                      board.is_corporate ? 'bg-primary-100' : 'bg-green-100'
                    }`}>
                      {board.is_corporate ? (
                        <svg className="h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      ) : (
                        <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{board.organization_name}</p>
                      <p className="text-sm text-gray-600">{board.role}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs ${board.is_corporate ? 'text-primary-600' : 'text-green-600'}`}>
                          {board.is_corporate ? 'Corporate' : 'Nonprofit'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {board.start_date ? new Date(board.start_date).getFullYear() : '?'} - {board.is_current ? 'Present' : board.end_date ? new Date(board.end_date).getFullYear() : '?'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No board memberships on record</p>
            )}
          </CollapsibleSection>

          {/* Contact Information */}
          <CollapsibleSection
            title="Contact Information"
            subtitle="Verified contact details"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
          >
            {executive.contacts && executive.contacts.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {executive.contacts.map((contact, idx) => {
                  const typeLabels: Record<string, string> = {
                    email_work: 'Work Email',
                    email_personal: 'Personal Email',
                    phone_work: 'Work Phone',
                    phone_mobile: 'Mobile',
                    linkedin: 'LinkedIn',
                    twitter: 'Twitter',
                    website: 'Website',
                  };
                  const isLink = contact.contact_type.includes('linkedin') ||
                                contact.contact_type.includes('twitter') ||
                                contact.contact_type === 'website';
                  const isGuessed = contact.notes?.toLowerCase().includes('generated') ||
                                   contact.notes?.toLowerCase().includes('pattern');
                  const showWarning = !contact.is_verified && (isGuessed || contact.confidence === 'low');

                  return (
                    <div key={idx} className={`py-3 flex items-center justify-between ${showWarning ? 'opacity-75' : ''}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{typeLabels[contact.contact_type] || contact.contact_type}</span>
                          {showWarning && (
                            <span className="inline-flex items-center rounded-full bg-yellow-100 px-1.5 py-0.5 text-xs font-medium text-yellow-800">
                              Guessed
                            </span>
                          )}
                        </div>
                        {isLink ? (
                          <a
                            href={contact.value.startsWith('http') ? contact.value : `https://${contact.value}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-600 hover:text-primary-500"
                          >
                            {contact.value}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-900">{contact.value}</p>
                        )}
                      </div>
                      {contact.is_verified && (
                        <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">No contact information available</p>
            )}
          </CollapsibleSection>

          {/* Source Index */}
          <CollapsibleSection
            title="Source Index"
            subtitle="All sources accessed with dates"
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          >
            <SourceIndex
              sourceTypes={executive.source_types}
              verificationSources={executive.verification_sources}
              bioFragments={executive.bio?.fragments}
              lastVerified={executive.last_verified}
            />
          </CollapsibleSection>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <div>
              Generated by Dossier Corporate Intelligence Platform
            </div>
            <div className="flex items-center gap-4">
              {executive.created_at && (
                <span>Created: {formatDate(executive.created_at)}</span>
              )}
              {executive.updated_at && (
                <span>Updated: {formatDate(executive.updated_at)}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .sticky { position: static; }
          button { display: none; }
        }
      `}</style>
    </div>
  );
}

export default ExecutiveDossierView;
